import React, { useEffect, useState } from "react";
import { hrApi } from "../../services/hrApi";

export default function ProjectResourcesCard({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [people, setPeople] = useState([]);
const inputClass =
  "w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white placeholder-gray-400 dark:placeholder-neutral-500";
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    person_id: "",
    role_on_project: "project_manager",
    role_name: "",
    allocation_percent: 100,
    start_date: "",
    end_date: "",
    status: "active",
    notes: "",
  });

  const [editingResource, setEditingResource] = useState(null);
const [editSaving, setEditSaving] = useState(false);
const [editForm, setEditForm] = useState({
  person_id: "",
  role_on_project: "project_manager",
  role_name: "",
  allocation_percent: 100,
  start_date: "",
  end_date: "",
  status: "active",
  notes: "",
});

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function openEditResource(row) {
  setEditingResource(row);
  setEditForm({
    person_id: row.person_id ? String(row.person_id) : "",
    role_on_project: row.role_on_project || "project_manager",
    role_name: row.role_name || "",
    allocation_percent: row.allocation_percent ?? 100,
    start_date: toDateInput(row.start_date),
    end_date: toDateInput(row.end_date),
    status: row.status || "active",
    notes: row.notes || "",
  });
}

function getResourceStatusBadge(status) {
  const map = {
    planned: "bg-gray-100 text-gray-700",
    active: "bg-blue-100 text-blue-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

function getSourceBadge(source) {
  if (source === "auto") {
    return <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">auto</span>;
  }
  return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">manual</span>;
}

async function handleUpdateResource(e) {
  e.preventDefault();

  if (!editingResource?.id) return;

  try {
    setEditSaving(true);

    await hrApi.updateProjectResource(editingResource.id, {
      person_id: editForm.person_id ? Number(editForm.person_id) : null,
      role_on_project: editForm.role_on_project,
      role_name: editForm.role_name || null,
      allocation_percent: Number(editForm.allocation_percent || 100),
      start_date: editForm.start_date || null,
      end_date: editForm.end_date || null,
      status: editForm.status || "active",
      notes: editForm.notes || null,
    });

    setEditingResource(null);
    await load();
  } catch (err) {
    console.error("Failed to update project resource:", err);
    alert(err?.response?.data?.message || "Failed to update project resource.");
  } finally {
    setEditSaving(false);
  }
}

  async function load() {
    try {
      setLoading(true);

      const [resourcesRes, peopleRes] = await Promise.all([
        hrApi.getProjectResources(projectId),
        hrApi.getPeople({ status: "active" }),
      ]);

      setResources(resourcesRes.data?.data || []);
      setPeople(peopleRes.data?.data || []);
    } catch (err) {
      console.error("Failed to load project resources:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (projectId) load();
  }, [projectId]);

  async function handleCreateResource(e) {
    e.preventDefault();

    try {
      setSaving(true);

      if (!form.person_id) {
        alert("Person is required.");
        return;
      }

      if (!form.role_on_project) {
        alert("Role on project is required.");
        return;
      }

      await hrApi.createProjectResource({
        project_id: projectId,
        person_id: Number(form.person_id),
        role_on_project: form.role_on_project,
        role_name: form.role_name || null,
        allocation_percent: Number(form.allocation_percent || 100),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status || "active",
        notes: form.notes || null,
      });

      setShowModal(false);
      setForm({
        person_id: "",
        role_on_project: "project_manager",
        role_name: "",
        allocation_percent: 100,
        start_date: "",
        end_date: "",
        status: "active",
        notes: "",
      });

      await load();
    } catch (err) {
      console.error("Failed to create project resource:", err);
      alert(err?.response?.data?.message || "Failed to create project resource.");
    } finally {
      setSaving(false);
    }
  }

  function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("sr-RS");
}

  async function handleDeleteResource(id) {
    const confirmed = window.confirm("Delete this project assignment?");
    if (!confirmed) return;

    try {
      await hrApi.deleteProjectResource(id);
      await load();
    } catch (err) {
      console.error("Failed to delete resource:", err);
      alert(err?.response?.data?.message || "Failed to delete resource.");
    }
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm p-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Project Resources</h2>
          <p className="text-sm text-gray-500">People assigned to this project.</p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-xl bg-black text-white"
        >
          Add resource
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading resources...</div>
      ) : resources.length === 0 ? (
        <div className="text-sm text-gray-500">No resources assigned yet.</div>
      ) : (
        <div className="space-y-3">
          {resources.map((row) => (
            <div
              key={row.id}
              className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-medium">
                    {row.full_name || row.display_name || `${row.first_name || ""} ${row.last_name || ""}`.trim() || "Unknown person"}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
  <span>{row.role_name || row.role_on_project}</span>
  <span>·</span>
  <span>{row.position_title || "-"}</span>
  <span>·</span>
  {getResourceStatusBadge(row.status)}
</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
  <div
    className="bg-blue-500 h-2 rounded-full"
    style={{ width: `${row.allocation_percent}%` }}
  />
</div>

                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                    {row.allocation_percent}% allocation
                  </span>
                  <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
  {row.assignment_source || "manual"}
</span>

<button
  onClick={() => openEditResource(row)}
  disabled={row.assignment_source === "auto"}
  className="opacity-80 disabled:opacity-40"
>
  Edit
</button>
                  <button
                    onClick={() => handleDeleteResource(row.id)}
                    className="px-3 py-1 rounded-lg border border-red-300 text-red-700 bg-red-50 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                <div><span className="text-gray-500">Start:</span> {fmtDate(row.start_date)}</div>
                <div><span className="text-gray-500">End:</span> {fmtDate(row.end_date)}</div>
                <div><span className="text-gray-500">Email:</span> {row.email_work || "-"}</div>
              </div>

              {row.notes && (
                <div className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">
                  {row.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Add project resource</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateResource} className="space-y-4">
              <select className={inputClass}
                value={form.person_id}
                onChange={(e) => setForm((s) => ({ ...s, person_id: e.target.value }))}
                required
              >
                <option value="">Select person</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || `${p.first_name} ${p.last_name}`}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className={inputClass}
                  value={form.role_on_project}
                  onChange={(e) => setForm((s) => ({ ...s, role_on_project: e.target.value }))}
                >
                  <option value="project_manager">Project manager</option>
                  <option value="account_manager">Account manager</option>
                  <option value="design_owner">Design owner</option>
                  <option value="procurement_owner">Procurement owner</option>
                  <option value="production_owner">Production owner</option>
                  <option value="installation_owner">Installation owner</option>
                  <option value="logistics_owner">Logistics owner</option>
                  <option value="finance_owner">Finance owner</option>
                  <option value="support">Support</option>
                  <option value="observer">Observer</option>
                </select>

                <input className={inputClass}
                  placeholder="Role display name"
                  value={form.role_name}
                  onChange={(e) => setForm((s) => ({ ...s, role_name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input className={inputClass}
                  type="number"
                  placeholder="Allocation %"
                  value={form.allocation_percent}
                  onChange={(e) => setForm((s) => ({ ...s, allocation_percent: e.target.value }))}
                />
                <input className={inputClass}
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))}
                />
                <input className={inputClass}
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))}
                />
              </div>

              <select className={inputClass}
                value={form.status}
                onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
              >
                <option value="planned">Planned</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <textarea className={`${inputClass} min-h-[100px]`}
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
              />

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-xl border"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-xl bg-black text-white"
                >
                  {saving ? "Saving..." : "Save resource"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

{editingResource && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
    <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-black dark:text-white">
          Edit project resource
        </h3>
        <button
          onClick={() => setEditingResource(null)}
          className="text-gray-500 dark:text-neutral-400"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleUpdateResource} className="space-y-4">
        <select
          className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          value={editForm.person_id}
          onChange={(e) => setEditForm((s) => ({ ...s, person_id: e.target.value }))}
        >
          <option value="">Select person</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name || `${p.first_name} ${p.last_name}`}
            </option>
          ))}
        </select>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
            value={editForm.role_on_project}
            onChange={(e) => setEditForm((s) => ({ ...s, role_on_project: e.target.value }))}
          >
            <option value="project_manager">Project manager</option>
            <option value="account_manager">Account manager</option>
            <option value="design_owner">Design owner</option>
            <option value="procurement_owner">Procurement owner</option>
            <option value="production_owner">Production owner</option>
            <option value="installation_owner">Installation owner</option>
            <option value="logistics_owner">Logistics owner</option>
            <option value="finance_owner">Finance owner</option>
            <option value="support">Support</option>
            <option value="observer">Observer</option>
          </select>

          <input
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
            placeholder="Role display name"
            value={editForm.role_name}
            onChange={(e) => setEditForm((s) => ({ ...s, role_name: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="number"
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
            placeholder="Allocation %"
            value={editForm.allocation_percent}
            onChange={(e) => setEditForm((s) => ({ ...s, allocation_percent: e.target.value }))}
          />

          <input
            type="date"
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
            value={editForm.start_date}
            onChange={(e) => setEditForm((s) => ({ ...s, start_date: e.target.value }))}
          />

          <input
            type="date"
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
            value={editForm.end_date}
            onChange={(e) => setEditForm((s) => ({ ...s, end_date: e.target.value }))}
          />
        </div>

        <select
          className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          value={editForm.status}
          onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))}
        >
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <textarea
          className="w-full min-h-[100px] px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          placeholder="Notes"
          value={editForm.notes}
          onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))}
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setEditingResource(null)}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={editSaving}
            className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
          >
            {editSaving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

    </div>
  );
}